/**
 * Action Tracker Hook (Orchestrator)
 *
 * Main hook for triggering and tracking long-running actions.
 * Wraps Zustand store + N8N service calls + status polling + notifications.
 *
 * Usage:
 *   const { startEnrich, startOutreach, startExport, isProcessingAction } = useActionTracker();
 */

import { useCallback } from 'react';
import {
  n8nService,
  isInsufficientCreditsError,
  formatInsufficientCreditsMessage,
} from '../components/core/services/n8n.service';
import { campaignReportExportService } from '../services/campaignReportExport.service';
import {
  useActiveActions,
  useHasActiveAction,
  useActionTrackerActions,
  type ActionType,
  type TrackedAction,
} from '../stores/actionTracker.store';
import { useActionStatusPolling } from './useActionStatusPolling';
import { notificationsApi } from '../services/notifications/notificationsApi';
import {
  showEnrichToast,
  showInsufficientCreditsToast,
  dismissEnrichToast,
} from '../utils/toast';
import { openBuyCredits } from '../stores/uiStore';
import type { CampaignReportCandidate } from '../utils/leadReports';

/**
 * Handle the case where n8n's credit checker rejected the action because the
 * client lacks credits. Dismisses any optimistic toast, surfaces a warning
 * toast + opens the global Buy Credits modal, and marks the tracked action
 * as failed via the caller's `failAction`. Returns true if it handled the error.
 */
const handleInsufficientCredits = (
  error: unknown,
  actionId: string,
  optimisticToastId: string | null,
  failAction: (id: string, message: string) => void,
): boolean => {
  // Duck-type check (not instanceof) — bundlers can split the class identity across
  // chunks, which would silently drop the Buy Credits CTA in production.
  if (!isInsufficientCreditsError(error)) return false;

  if (optimisticToastId) {
    // The blue "Enriching X" toast that fired before the request went out —
    // drop it so the orange "insufficient credits" toast is the only thing on screen.
    dismissEnrichToast(optimisticToastId);
  }

  // Build a clear message from the numbers n8n returns (need vs. have vs. shortfall).
  const message = formatInsufficientCreditsMessage(error.answer);
  showInsufficientCreditsToast(message);
  openBuyCredits();
  failAction(actionId, message);
  return true;
};

interface EnrichParams {
  campaignId: string;
  campaignName: string;
  leadIds: string[];
  leadNames: string[];
  leadCount: number;
  clientEmail: string;
  clientId?: string;
  clientName?: string;
}

interface OutreachParams extends EnrichParams {
  outreachType?: 'email' | 'linkedin' | 'both';
}

interface ExportParams {
  campaignId: string;
  campaignName: string;
  candidates: CampaignReportCandidate[];
  signature: string;
  userEmail?: string | null;
}

export interface UseActionTrackerReturn {
  startEnrich: (params: EnrichParams) => Promise<void>;
  startEnrichUpdate: (params: EnrichParams) => Promise<void>;
  startOutreach: (params: OutreachParams) => Promise<void>;
  startExport: (params: ExportParams) => Promise<void>;
  activeActions: TrackedAction[];
  isProcessingAction: (type?: ActionType) => boolean;
  isActionLoading: boolean;
}

const buildActionMessage = (type: 'enrich' | 'outreach', leadNames: string[], leadCount: number) => {
  const verb = type === 'enrich' ? 'Enriching' : 'Starting outreach for';
  const firstName = leadNames[0] || 'Lead';
  if (leadCount === 1) return `${verb} ${firstName}`;
  return `${verb} ${leadCount} leads: ${firstName}, ...`;
};

export const useActionTracker = (userEmail?: string, userId?: string | null): UseActionTrackerReturn => {
  const storeActions = useActionTrackerActions();
  const activeActions = useActiveActions();
  const hasActiveAction = useHasActiveAction();
  const hasActiveEnrich = useHasActiveAction('enrich');
  const hasActiveOutreach = useHasActiveAction('outreach');

  // Start background polling for status changes
  useActionStatusPolling(userEmail);

  const startEnrich = useCallback(
    async (params: EnrichParams) => {
      const actionId = storeActions.startAction({
        type: 'enrich',
        leadIds: params.leadIds,
        leadNames: params.leadNames,
        campaignId: params.campaignId,
        campaignName: params.campaignName,
        leadCount: params.leadCount,
        targetStatus: 'enriched',
      });

      // Optimistic toast only — the tracker's own toast IS the start
      // notification. Writing a bell notification for the same event made the
      // long-poll fire a second toast seconds later (the "2 notifications" bug).
      const toastMsg = buildActionMessage('enrich', params.leadNames, params.leadCount);
      const optimisticToastId = showEnrichToast(toastMsg);

      try {
        await n8nService.bulkEnrichLeads({
          campaign_id: params.campaignId,
          lead_ids: params.leadIds,
          client_email: params.clientEmail,
          client_id: params.clientId,
          client_name: params.clientName,
          action: 'enrich',
        });

        storeActions.updateAction(actionId, { status: 'running' });
      } catch (error) {
        // Credit-checker rejection — surface a warning + open Buy Credits modal.
        // handleInsufficientCredits returns true when it handled the error.
        if (handleInsufficientCredits(error, actionId, optimisticToastId, storeActions.failAction)) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Enrichment failed to start';
        storeActions.failAction(actionId, message);
      }
    },
    [storeActions, userId]
  );

  const startEnrichUpdate = useCallback(
    async (params: EnrichParams) => {
      const actionId = storeActions.startAction({
        type: 'enrich',
        leadIds: params.leadIds,
        leadNames: params.leadNames,
        campaignId: params.campaignId,
        campaignName: params.campaignName,
        leadCount: params.leadCount,
        targetStatus: 'enriched',
      });

      const toastMsg = `Updating enrichment for ${params.leadCount === 1 ? (params.leadNames[0] || 'Lead') : `${params.leadCount} leads: ${params.leadNames[0] || 'Lead'}, ...`}`;
      const optimisticToastId = showEnrichToast(toastMsg);

      try {
        await n8nService.enrichUpdateLeads({
          campaign_id: params.campaignId,
          lead_ids: params.leadIds,
          client_email: params.clientEmail,
          client_id: params.clientId,
          client_name: params.clientName,
        });

        storeActions.updateAction(actionId, { status: 'running' });
      } catch (error) {
        if (handleInsufficientCredits(error, actionId, optimisticToastId, storeActions.failAction)) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Enrichment update failed to start';
        storeActions.failAction(actionId, message);
      }
    },
    [storeActions, userId]
  );

  const startOutreach = useCallback(
    async (params: OutreachParams) => {
      const actionId = storeActions.startAction({
        type: 'outreach',
        leadIds: params.leadIds,
        leadNames: params.leadNames,
        campaignId: params.campaignId,
        campaignName: params.campaignName,
        leadCount: params.leadCount,
        targetStatus: 'outreached',
      });

      // Create bell notification
      let notificationId: string | null = null;
      if (userId) {
        try {
          const msg = buildActionMessage('outreach', params.leadNames, params.leadCount);
          const firstLeadId = params.leadIds[0] || '';
          const notification = await notificationsApi.create(msg, `action::${actionId}::${params.campaignId}::${firstLeadId}`);
          notificationId = String(notification.id);
          storeActions.updateAction(actionId, { notificationId });
        } catch {
          // notification is non-critical
        }
      }

      try {
        await n8nService.sendToOutreach({
          campaign_id: params.campaignId,
          lead_ids: params.leadIds,
          outreach_type: params.outreachType,
          client_email: params.clientEmail,
          client_id: params.clientId,
          client_name: params.clientName,
          action: 'outreach',
        });

        storeActions.updateAction(actionId, { status: 'running' });
      } catch (error) {
        if (handleInsufficientCredits(error, actionId, null, storeActions.failAction)) {
          if (notificationId) {
            notificationsApi.remove(notificationId).catch(() => {});
          }
          return;
        }
        const message = error instanceof Error ? error.message : 'Outreach failed to start';
        storeActions.failAction(actionId, message);
        if (notificationId) {
          notificationsApi.remove(notificationId).catch(() => {});
        }
      }
    },
    [storeActions, userId]
  );

  const startExport = useCallback(
    async (params: ExportParams) => {
      const actionId = storeActions.startAction({
        type: 'export',
        leadIds: params.candidates.map((c) => c.leadId),
        campaignId: params.campaignId,
        campaignName: params.campaignName,
        leadCount: params.candidates.length,
        targetStatus: 'exported',
      });

      try {
        const result = await campaignReportExportService.exportCampaignReports({
          campaignId: params.campaignId,
          campaignName: params.campaignName,
          signature: params.signature,
          candidates: params.candidates,
          userEmail: params.userEmail,
        });

        if (result.success) {
          storeActions.completeAction(actionId);

          // Auto-download if we have URLs
          if (result.downloadUrls?.length) {
            for (const url of result.downloadUrls) {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          } else if (result.downloadUrl) {
            window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
          }
        } else {
          storeActions.failAction(actionId, 'Export failed');
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Export failed';
        storeActions.failAction(actionId, message);
      }
    },
    [storeActions]
  );

  const isProcessingAction = useCallback(
    (type?: ActionType) => {
      if (type === 'enrich') return hasActiveEnrich;
      if (type === 'outreach') return hasActiveOutreach;
      return hasActiveAction;
    },
    [hasActiveAction, hasActiveEnrich, hasActiveOutreach]
  );

  return {
    startEnrich,
    startEnrichUpdate,
    startOutreach,
    startExport,
    activeActions,
    isProcessingAction,
    isActionLoading: hasActiveAction,
  };
};
